import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useViewAs } from '../context/ViewAsContext';
import * as permissions from '../utils/permissions';

/**
 * Hook that combines Auth and ViewAs contexts to provide effective user permissions
 * This should be used instead of useAuth when you need to respect ViewAs role switching
 */
export const useEffectiveAuth = () => {
  const auth = useAuth();
  const viewAs = useViewAs();
  
  // Use effective user from ViewAs context if available, otherwise use regular user
  const effectiveUser = viewAs?.effectiveUser || auth.user;
  
  // Create permission helpers using effective user
  const effectivePermissions = useMemo(() => ({
    canCreateEvents: () => permissions.canCreateEvents(effectiveUser),
    canCreateUsers: () => permissions.canCreateUsers(effectiveUser),
    canCreateAdmins: () => permissions.canCreateAdmins(effectiveUser),
    canDeleteAnyEvent: () => permissions.canDeleteAnyEvent(effectiveUser),
    canDeleteAnyUser: () => permissions.canDeleteAnyUser(effectiveUser),
    canSeeAllEvents: () => permissions.canSeeAllEvents(effectiveUser),
    canSeeAllUsers: () => permissions.canSeeAllUsers(effectiveUser),
    isSuperAdmin: () => permissions.isSuperAdmin(effectiveUser),
    isEventAdmin: () => permissions.isEventAdmin(effectiveUser),
    isAdmin: () => permissions.isAdmin(effectiveUser),
    getAllowedRolesToCreate: () => permissions.getAllowedRolesToCreate(effectiveUser),
    canDeleteUser: (targetUser) => permissions.canDeleteUser(effectiveUser, targetUser),
    canEditUser: (targetUser) => permissions.canEditUser(effectiveUser, targetUser),
    getNavigationItems: () => permissions.getNavigationItems(effectiveUser),
  }), [effectiveUser]);

  return {
    // Original auth properties
    ...auth,
    
    // Effective user and permissions (respects ViewAs)
    effectiveUser,
    effectivePermissions,
    
    // ViewAs properties
    isViewingAs: viewAs?.isViewingAs || false,
    viewingRole: viewAs?.viewingRole,
    originalUser: auth.user, // Always the actual logged-in user
  };
};
